import React, { useState, useEffect, useRef } from "react";

import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";

import { i18n } from "../../translate/i18n";
import {
  FormControl as MuiFormControl,
  InputLabel as MuiInputLabel,
  InputAdornment,
  MenuItem as MuiMenuItem,
  Select as MuiSelect,
  Stack,
  TextField as MuiTextField
} from "@mui/material";
import { Bot, BriefcaseBusiness, Clock3 } from "lucide-react";
import ChatiaDialogActions from "../ChatiaDialogActions";
import { CloseXIcon } from "../ChatiaIcons";
import CustomToggleSwitch from "../CustomToggleSwitch";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette.type === "dark";

  return {
    root: {
      display: "flex",
      flexWrap: "wrap",
    },
    dialogPaper: {
      ...(theme.chatiaDS?.dialogPaper || {}),
      "& .MuiDialogContent-root": {
        flex: 1,
        overflowY: "auto !important",
        overflowX: "hidden",
        background: isDark ? "#121212" : "#ffffff",
        ...theme.scrollbarStyles,
      },
      "& .MuiDialogActions-root": {
        flexShrink: 0,
        padding: "12px 16px",
        background: isDark ? "#121212" : "#ffffff",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? `${theme.palette.primary.main} !important` : undefined,
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? `${theme.palette.primary.main} !important` : undefined,
      },
    },
    header: {
      ...(theme.chatiaDS?.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(theme.chatiaDS?.headerTitle || {}),
    },
    closeButton: {
      backgroundColor: theme.chatiaDS?.closeButton?.backgroundColor || "rgba(255,255,255,0.08)",
      color: theme.chatiaDS?.closeButton?.color || "#ffffff",
      transition: theme.chatiaDS?.closeButton?.transition || "all 0.2s ease",
      "&:hover": {
        backgroundColor: theme.chatiaDS?.closeButtonHover?.backgroundColor || "rgba(255,255,255,0.18)",
        color: "#ffffff",
      },
    },
    content: {
      padding: "18px 20px !important",
      maxHeight: "70vh",
    },
    bodyCard: {
      gap: 12,
      display: "flex",
      flexDirection: "column",
    },
    infoCard: {
      background: theme.mode === "dark" ? "#16191f" : "#ffffff",
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
      borderRadius: 12,
      padding: 16,
      boxShadow: "0 10px 28px rgba(0, 0, 0, 0.16)",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 14,
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: 0.1,
    },
    cardIcon: {
      width: 32,
      height: 32,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.04)",
      border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
      color: theme.palette.primary.main,
      "& svg": {
        color: theme.palette.primary.main,
        stroke: theme.palette.primary.main,
      },
    },
    row: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      flexWrap: "wrap",
    },
    timeRow: {
      display: "flex",
      gap: theme.spacing(2),
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      flexWrap: "wrap",
    },
    daysRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
      marginTop: theme.spacing(1),
    },
    helperText: {
      display: "block",
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
      fontSize: 13,
      marginBottom: 8,
    },
    toggleCard: {
      display: "flex",
      flexDirection: "column",
      padding: "10px 16px",
      borderRadius: 10,
      border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
      background: theme.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)",
      marginBottom: 4,
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
        borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.20)" : "rgba(15,23,42,0.20)",
      },
    },
    toggleCardRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      gap: 12,
    },
    toggleLabel: {
      fontSize: "0.9rem",
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      fontWeight: 500,
    },
    dayCheckboxLabel: {
      display: "inline-flex",
      alignItems: "center",
      cursor: "pointer",
      userSelect: "none",
      padding: "7px 10px",
      borderRadius: 10,
      border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
      background: theme.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)",
      transition: "all 0.2s ease",
      "&:hover": {
        background: "rgba(255,255,255,0.05)",
        borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.20)" : "rgba(15,23,42,0.20)",
      },
    },
    cyberpunkCheckbox: {
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
      width: 16,
      height: 16,
      border: `2px solid ${theme.palette.primary.main}`,
      borderRadius: 5,
      backgroundColor: "transparent",
      display: "inline-block",
      position: "relative",
      margin: 0,
      marginRight: 8,
      padding: 0,
      cursor: "pointer",
      flexShrink: 0,
      "&::before": {
        content: '""',
        backgroundColor: theme.palette.primary.main,
        display: "block",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%) scale(0)",
        width: 8,
        height: 8,
        borderRadius: 3,
        transition: "all 0.3s ease-in-out",
      },
      "&:checked::before": {
        transform: "translate(-50%, -50%) scale(1)",
      },
    },
    dayCheckboxText: {
      fontSize: 13,
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      lineHeight: 1,
    },
  };
});

const getUnitOptions = () => [
  { value: "seconds", label: i18n.t("flowBuilderModals.intervalModal.fields.unitSeconds") },
  { value: "minutes", label: i18n.t("flowBuilderModals.intervalModal.fields.unitMinutes") },
  { value: "hours", label: i18n.t("flowBuilderModals.intervalModal.fields.unitHours") },
  { value: "days", label: i18n.t("flowBuilderModals.intervalModal.fields.unitDays") }
];

const MAX_VALUES = {
  seconds: 120,
  minutes: 120,
  hours: 72,
  days: 30
};

const getDaysOfWeek = () => [
  { value: 1, label: i18n.t("flowBuilderModals.intervalModal.fields.dayMon") },
  { value: 2, label: i18n.t("flowBuilderModals.intervalModal.fields.dayTue") },
  { value: 3, label: i18n.t("flowBuilderModals.intervalModal.fields.dayWed") },
  { value: 4, label: i18n.t("flowBuilderModals.intervalModal.fields.dayThu") },
  { value: 5, label: i18n.t("flowBuilderModals.intervalModal.fields.dayFri") },
  { value: 6, label: i18n.t("flowBuilderModals.intervalModal.fields.daySat") },
  { value: 0, label: i18n.t("flowBuilderModals.intervalModal.fields.daySun") }
];

const DEFAULT_BUSINESS_HOURS = {
  enabled: false,
  startTime: "08:00",
  endTime: "18:00",
  daysOfWeek: [1, 2, 3, 4, 5]
};

const FlowBuilderIntervalModal = ({
  open,
  onSave,
  data,
  onUpdate,
  close
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);

  const [timerValue, setTimerValue] = useState(0);
  const [timerUnit, setTimerUnit] = useState("seconds");
  const [businessHours, setBusinessHours] = useState({ ...DEFAULT_BUSINESS_HOURS });
  const [aiTimeoutSeconds, setAiTimeoutSeconds] = useState(0);
  const [activeModal, setActiveModal] = useState(false);

  useEffect(() => {
    if (open === "edit") {
      const d = data.data;
      if (d.unit) {
        setTimerValue(d.value || 0);
        setTimerUnit(d.unit || "seconds");
        setBusinessHours(d.businessHours || { ...DEFAULT_BUSINESS_HOURS });
        setAiTimeoutSeconds(d.aiTimeoutSeconds || 0);
      } else if (d.sec !== undefined) {
        setTimerValue(d.sec);
        setTimerUnit("seconds");
        setBusinessHours({ ...DEFAULT_BUSINESS_HOURS });
      }
      setActiveModal(true);
    } else if (open === "create") {
      setTimerValue(0);
      setTimerUnit("seconds");
      setBusinessHours({ ...DEFAULT_BUSINESS_HOURS });
      setAiTimeoutSeconds(0);
      setActiveModal(true);
    }
    return () => {
      isMounted.current = false;
    };
  }, [open]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleToggleDay = (day) => {
    setBusinessHours(prev => {
      const days = prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day];
      return { ...prev, daysOfWeek: days };
    });
  };

  const handleSaveContact = () => {
    const val = parseInt(timerValue);
    if (!val || val <= 0) {
      return toast.error(i18n.t("flowBuilderModals.intervalModal.validation.addValue"));
    }
    const max = MAX_VALUES[timerUnit];
    if (val > max) {
      return toast.error(
        i18n.t("flowBuilderModals.intervalModal.validation.maxTime", {
          max,
          unit: getUnitOptions().find(u => u.value === timerUnit)?.label || timerUnit
        })
      );
    }
    if (businessHours.enabled && businessHours.daysOfWeek.length === 0) {
      return toast.error(i18n.t("flowBuilderModals.intervalModal.validation.addValue"));
    }

    const payload = {
      value: val,
      unit: timerUnit,
      businessHours: { ...businessHours },
      aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: payload
      });
    } else if (open === "create") {
      onSave(payload);
    }
    handleClose();
  };

  const title = open === "create"
    ? i18n.t("flowBuilderModals.intervalModal.titleAdd")
    : i18n.t("flowBuilderModals.intervalModal.titleEdit");

  const selectFieldSx = {
    minWidth: 180,
    mt: "8px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px !important",
      minHeight: 40,
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "12px !important",
      borderColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.18)" : "rgba(15, 23, 42, 0.18)",
    },
    "& .MuiInputBase-root": {
      borderRadius: "12px !important",
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.mode === "dark" ? "#16191f" : "#ffffff",
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
      px: "4px",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    },
    "& .MuiSvgIcon-root": {
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    },
  };

  const selectMenuProps = {
    PaperProps: {
      sx: {
        mt: 1,
        borderRadius: "14px",
        boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
        border: "none",
        backgroundImage: "none",
        overflow: "hidden",
        "& .MuiMenu-list": {
          padding: "6px",
        },
        "& .MuiMenuItem-root": {
          borderRadius: "8px",
          padding: "9px 12px",
          marginBottom: "4px",
          fontSize: 14,
          fontWeight: 500,
          "&.Mui-selected": {
            background: "rgba(6, 207, 156, 0.10)",
          },
          "&.Mui-selected:hover": {
            background: "rgba(6, 207, 156, 0.16)",
          },
        },
      },
    },
  };

  const timeFieldSx = {
    flex: 1,
    minWidth: 180,
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      minHeight: 40,
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      background: theme.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.14)",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.24)",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
      borderWidth: 1,
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.mode === "dark" ? "#16191f" : "#ffffff",
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
      px: "4px",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    },
    "& .MuiInputAdornment-root svg": {
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    },
    "& input::-webkit-calendar-picker-indicator": {
      opacity: 0,
      cursor: "pointer",
    },
  };

  const CardTitle = ({ icon: Icon, children }) => (
    <div className={classes.cardHeader}>
      <span className={classes.cardIcon}>
        <Icon size={16} />
      </span>
      <span>{children}</span>
    </div>
  );

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>{title}</Typography>
          <IconButton size="small" onClick={handleClose} className={classes.closeButton}>
            <CloseXIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
        <Stack>
          <DialogContent className={classes.content}>
            <Stack className={classes.bodyCard}>
              <Stack className={classes.infoCard}>
                <CardTitle icon={Clock3}>
                  {i18n.t("flowBuilderModals.intervalModal.fields.interval")}
                </CardTitle>
                <div className={classes.row}>
                  <TextField
                    label={i18n.t("flowBuilderModals.intervalModal.fields.timeValue")}
                    name="timerValue"
                    type="number"
                    value={timerValue}
                    onChange={(e) => setTimerValue(e.target.value)}
                    autoFocus
                    variant="outlined"
                    InputProps={{ inputProps: { min: 0, max: MAX_VALUES[timerUnit] } }}
                    margin="dense"
                    style={{ flex: 1, minWidth: 220 }}
                  />
                  <MuiFormControl variant="outlined" size="small" sx={selectFieldSx}>
                    <MuiInputLabel id="interval-unit-selection-label">
                      {i18n.t("flowBuilderModals.intervalModal.fields.unit")}
                    </MuiInputLabel>
                    <MuiSelect
                      labelId="interval-unit-selection-label"
                      id="intervalUnit"
                      value={timerUnit}
                      onChange={(e) => setTimerUnit(e.target.value)}
                      label={i18n.t("flowBuilderModals.intervalModal.fields.unit")}
                      MenuProps={selectMenuProps}
                    >
                      {getUnitOptions().map(opt => (
                        <MuiMenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MuiMenuItem>
                      ))}
                    </MuiSelect>
                  </MuiFormControl>
                </div>
              </Stack>

              <Stack className={classes.infoCard}>
                <CardTitle icon={BriefcaseBusiness}>
                  {i18n.t("flowBuilderModals.intervalModal.fields.businessHours")}
                </CardTitle>
                <div className={classes.toggleCard}>
                  <div className={classes.toggleCardRow}>
                    <span className={classes.toggleLabel}>
                      {i18n.t("flowBuilderModals.intervalModal.fields.businessHours")}
                    </span>
                    <CustomToggleSwitch
                      checked={businessHours.enabled}
                      onChange={(e) =>
                        setBusinessHours(prev => ({ ...prev, enabled: e.target.checked }))
                      }
                    />
                  </div>
                </div>

                {businessHours.enabled && (
                  <div style={{ marginTop: 8 }}>
                    <Typography className={classes.helperText}>
                      {i18n.t("flowBuilderModals.intervalModal.fields.businessHoursHelp")}
                    </Typography>
                    <div className={classes.timeRow}>
                      <MuiTextField
                        label={i18n.t("flowBuilderModals.intervalModal.fields.startTime")}
                        type="time"
                        value={businessHours.startTime}
                        onChange={(e) =>
                          setBusinessHours(prev => ({ ...prev, startTime: e.target.value }))
                        }
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        sx={timeFieldSx}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Clock3 size={18} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <MuiTextField
                        label={i18n.t("flowBuilderModals.intervalModal.fields.endTime")}
                        type="time"
                        value={businessHours.endTime}
                        onChange={(e) =>
                          setBusinessHours(prev => ({ ...prev, endTime: e.target.value }))
                        }
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        sx={timeFieldSx}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Clock3 size={18} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                    <Typography className={classes.helperText}>
                      {i18n.t("flowBuilderModals.intervalModal.fields.daysOfWeek")}
                    </Typography>
                    <div className={classes.daysRow}>
                      {getDaysOfWeek().map(day => (
                        <label key={day.value} className={classes.dayCheckboxLabel}>
                          <input
                            type="checkbox"
                            className={classes.cyberpunkCheckbox}
                            checked={businessHours.daysOfWeek.includes(day.value)}
                            onChange={() => handleToggleDay(day.value)}
                          />
                          <span className={classes.dayCheckboxText}>{day.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </Stack>

              <Stack className={classes.infoCard}>
                <CardTitle icon={Bot}>
                  {i18n.t("flowBuilderModals.intervalModal.aiTimeout.title")}
                </CardTitle>
                <TextField
                  label={i18n.t("flowBuilderModals.intervalModal.aiTimeout.label")}
                  type="number"
                  variant="outlined"
                  margin="dense"
                  value={aiTimeoutSeconds}
                  onChange={e => setAiTimeoutSeconds(e.target.value)}
                  fullWidth
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText={aiTimeoutSeconds > 0
                    ? i18n.t("flowBuilderModals.intervalModal.aiTimeout.helperText", { seconds: aiTimeoutSeconds })
                    : ""}
                />
              </Stack>
            </Stack>
          </DialogContent>
          <ChatiaDialogActions
            type="button"
            onClick={() => handleSaveContact()}
            saveText={open === "create"
              ? i18n.t("flowBuilderModals.intervalModal.buttonAdd")
              : i18n.t("flowBuilderModals.intervalModal.buttonEdit")}
          />
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderIntervalModal;
